import React from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { UserContext, useNavigateWithContext } from 'state';
import { useAPIData } from 'dal/useAPIData';
import Button from 'components/button';
import Icon from 'components/icon';
import useMobile from 'hooks/useMobile';
import { useOrgId } from 'hooks/useSessionVariables';
import ShowingItem from './showing-item';
import toast from 'react-hot-toast';

const Wrapper = styled.main`
  height: 100%;
  flex: 1;
  overflow-y: auto;
  .loading {
    display: block;
    margin: 30px auto;
  }
`;

const Container = styled.div`
  margin: 0 auto;
  padding: 0 30px 30px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 1em;
  margin: 20px 0 15px;
  display: flex;
  justify-content: space-between;
  width: 100%;
  > span {
    font-size: 1.5em;
  }
  > button {
    padding: 5px 20px !important;
  }
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  gap: 20px;
  > div {
    width: 400px;
    border: solid 1px #dfe7ed;
    overflow-y: auto;
  }
  &.mobile-grid {
    > div {
      width: 100%;
      height: 400px;
    }
  }
`;

export default ({ previewMode }) => {
  const { isTabletOrMobile } = useMobile();
  const orgId = useOrgId();
  const { userContext } = React.useContext(UserContext);
  const [refreshKey, setRefreshKey] = React.useState(new Date().getTime());
  let navigate = useNavigateWithContext();
  const { showingId } = useParams();

  // this loads all applications for the user
  const [showingList, listFetchInProgress, dataFetched, failedFetch] = useAPIData(
    'applicationItems.LIST_APPLICATION_ITEMS',
    { userContext, filterCriteria: { type: 'showing', companyId: orgId } },
    {
      disableEmptyArgs: true,
      reFetchWhen: [refreshKey],
      onSuccess: (data) => {
        if (!data.list?.length && !previewMode) {
          navigate(`app/showing/create`);
        }
      },
      onError: (err) => {
        console.error({ errData: err.data, err });
        toast.error(err.data?.message ? err.data?.message : err.message || err.name);
      }
    }
  );

  if (!dataFetched) {
    return (
      <Wrapper>
        <Container>
          <Icon name="fa-duotone fa-spinner-third fa-spin loading" size={100} />
        </Container>
      </Wrapper>
    );
  }

  const filteredShowings = previewMode
    ? showingList?.list?.filter((showing) => showing._id === showingId)
    : showingList?.list;

  return (
    <Wrapper>
      <Container>
        <Header>
          <Title>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {previewMode && (
                <Button
                  variant="text"
                  onClick={() => navigate('app/showing')}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Icon name="fa-solid fa-chevron-left" size={16} />
                  <Trans i18nKey="showing.list.allTours">All Tours</Trans>
                </Button>
              )}
            </div>
            <Button variant="primary" onClick={() => navigate(`app/showing/create`)}>
              <Trans i18nKey="showing.actions.newTour">+ New Tour</Trans>
            </Button>
          </Title>
        </Header>
        <Grid className={isTabletOrMobile ? 'mobile-grid' : undefined}>
          {filteredShowings
            ?.sort((a, b) => {
              let firstDate = new Date(a?.updatedAt).getTime();
              let secondDate = new Date(b?.updatedAt).getTime();
              if (!isNaN(firstDate) && !isNaN(secondDate)) {
                return secondDate - firstDate;
              }
              if (isNaN(firstDate) && isNaN(secondDate)) {
                return 0;
              }
              return isNaN(firstDate) ? -1 : 1;
            })
            ?.map((showing, ix) => (
              <ShowingItem
                key={ix}
                showing={showing}
                navigate={navigate}
                refreshList={() => setRefreshKey(new Date().getTime())}
              />
            ))}
        </Grid>
      </Container>
    </Wrapper>
  );
};
